import { useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { Badge, Button, Nav, NavItem, NavLink } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import Spinners from "../../../components/Common/Spinner";
import { useGetClientBookingDetails } from "../../../api/booking";
import { isEligibleReplacementShift } from "../replacementInviteEligibility";
import { selectUser } from "../../../store/auth/selectors";
import {
  canAccessWorkersManagement,
  hasUnpaidInvoices,
} from "../clientBookingAccess";
import BookingDetailsTab from "./BookingDetailsTab";
import BookingAttendanceTab from "./BookingAttendanceTab";

const STATUS_COLOR_MAP = {
  processing: "primary",
  pending: "warning",
  approved: "success",
  rejected: "danger",
  cancelled: "secondary",
};

const MAIN_TABS = [
  { id: "details", label: "Details" },
  { id: "attendance", label: "Attendance" },
];

const ClientBookingDetails = () => {
  const { bookingSlug } = useParams();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [activeTab, setActiveTab] = useState("details");

  const { data, isLoading, error, refetch, isFetching } =
    useGetClientBookingDetails(bookingSlug);

  const booking = useMemo(() => data?.data?.data ?? data?.data ?? data, [data]);
  const statusColor = STATUS_COLOR_MAP[booking?.status] || "info";

  const showReplacementCta = useMemo(
    () =>
      (booking?.shiftPattern ?? []).some((s) => isEligibleReplacementShift(s)),
    [booking?.shiftPattern]
  );

  const showWorkersManagement = useMemo(
    () => canAccessWorkersManagement(booking, user?.tier),
    [booking, user?.tier]
  );

  const showPayInvoice = useMemo(
    () => hasUnpaidInvoices(booking),
    [booking]
  );

  if (isLoading && !booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Booking Details" />
          <Spinners setLoading={() => {}} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Booking Details" />
          <div className="alert alert-danger">
            Failed to load booking details.{" "}
            {error?.message || "Please try again later."}
          </div>
          <Button color="primary" onClick={() => refetch?.()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Bookings" breadcrumbItem="Booking Details" />
          <div className="text-center py-5">
            <div className="text-muted">
              <i
                className="mdi mdi-information-outline"
                style={{ fontSize: "48px" }}
              />
              <p className="mt-3">Booking not found.</p>
            </div>
            <Button
              color="primary"
              onClick={() => navigate("/client/bookings")}
            >
              Back to Bookings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title="Bookings"
          breadcrumbItem={`Booking: ${booking.slug}`}
        />

        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex gap-2">
            <Button color="light" onClick={() => navigate(-1)}>
              <i className="mdi mdi-arrow-left me-1" />
              Back
            </Button>
            <Button
              color="light"
              onClick={() => refetch?.()}
              disabled={isFetching}
              title="Refresh data"
            >
              <i
                className={`mdi ${
                  isFetching ? "mdi-loading mdi-spin" : "mdi-refresh"
                }`}
              />
            </Button>
            {showWorkersManagement ? (
              <Button
                color="primary"
                outline
                tag={Link}
                to={`/client/bookings/${bookingSlug}/workers-management`}
              >
                <i className="mdi mdi-account-group-outline me-1" />
                Manage workers
              </Button>
            ) : null}
            {showPayInvoice ? (
              <Button
                color="primary"
                tag={Link}
                to={`/client/bookings/${bookingSlug}/payment`}
              >
                <i className="mdi mdi-credit-card-outline me-1" />
                Pay invoice
              </Button>
            ) : null}
            {showReplacementCta ? (
              <Button
                color="primary"
                tag={Link}
                to={`/client/bookings/${bookingSlug}/find-replacements`}
              >
                Invite worker for replacement
              </Button>
            ) : null}
          </div>
          <Badge className={`bg-${statusColor} text-uppercase`}>
            {booking.status || "unknown"}
          </Badge>
        </div>

        <Nav className="nav-tabs nav-tabs-custom mb-4">
          {MAIN_TABS.map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                href="#"
                className={activeTab === tab.id ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab(tab.id);
                }}
              >
                {tab.label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {activeTab === "details" ? (
          <BookingDetailsTab booking={booking} />
        ) : (
          <BookingAttendanceTab booking={booking} />
        )}
      </div>
    </div>
  );
};

export default ClientBookingDetails;
